import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Chip,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import DisplayErrors from "src/components/display-errors";
import Iconify from "src/components/iconify/iconify";
import SimpleDialog from "src/components/simple-dialog";
import TruxCheckboxField from "src/components/inputs/trux-checkbox-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import TruxSimpleDateTimeField from "src/components/inputs/trux-simple-date-time-field";
import TruxAutocompleteField from "src/components/inputs/trux-autocomplete-field";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { Rental, RentalCustomer, RentalOrganizationSettings, RentalTrailer } from "src/models";
import axios, { endpoints, formatEndpoint } from "src/utils/axios";
import { Constants, isRentalTruckType, rentalTrailerType } from "src/constants";
import { RentalFileField } from "../trailers/rental-file-field";
import { collectChoiceGroups } from "../agreements/choice-extension";
import { collectCustomVariables } from "../agreements/custom-variable-extension";
import { isOngoingRental } from "./rental-status";
import { RentalDocuments } from "./rental-documents";

const STAGE_STEPS = ["Insurance", "Agreement", "Outbound", "Return"];

function InfoItem({ label, value }: { label: string; value?: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value || "-"}</Typography>
    </Box>
  );
}

// a collapsible block for one lifecycle stage; finished stages start
// collapsed so the dialog always opens on what matters now
function StageCard({
  title,
  chip,
  timestamp,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  chip: React.ReactNode;
  timestamp?: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        onClick={onToggle}
        sx={{ px: 2, py: 1.5, cursor: "pointer", userSelect: "none" }}
      >
        <Stack direction="row" alignItems="center" gap={1.5} flexGrow={1} flexWrap="wrap">
          <Typography variant="subtitle1">{title}</Typography>
          {chip}
          {timestamp && (
            <Typography variant="caption" color="text.secondary">
              {timestamp}
            </Typography>
          )}
        </Stack>
        <Iconify icon={expanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} width={20} />
      </Stack>
      {expanded && (
        <>
          <Divider />
          <Stack gap={2} sx={{ p: 2 }}>
            {children}
          </Stack>
        </>
      )}
    </Box>
  );
}

function stageStep(status: number) {
  if (status <= Constants.RENTAL_STATUS_INSURANCE_SUBMITTED) {
    return 0;
  }
  if (status <= Constants.RENTAL_STATUS_AGREEMENT_SUBMITTED) {
    return 1;
  }
  if (status === Constants.RENTAL_STATUS_AGREEMENT_CONFIRMED) {
    return 2;
  }
  if (status <= Constants.RENTAL_STATUS_RETURNED) {
    return 3;
  }
  return STAGE_STEPS.length;
}

// mirrors RentalOrganizationSetting.AgreementForRental on the backend
export function agreementTemplateForRental(
  organizationSettings: RentalOrganizationSettings | undefined,
  rental: Rental
): string {
  if (!organizationSettings) {
    return "";
  }
  let template = organizationSettings.agreementTemplate || "";
  const override = (organizationSettings.typeAgreements || []).find(
    (entry) => entry.trailerType === rental.trailerType
  );
  if (override?.agreementTemplate) {
    template = override.agreementTemplate;
  }
  if (rental.agreementName) {
    const named = (organizationSettings.namedAgreements || []).find(
      (entry) => entry.name === rental.agreementName
    );
    if (named?.agreementTemplate) {
      template = named.agreementTemplate;
    }
  }
  return template;
}

type RentalModalProps = {
  rentalModel: ModelSavingData<Rental>;
  rentals: Rental[];
  rentalTrailers: RentalTrailer[];
  rentalCustomers: RentalCustomer[];
  organizationSettings?: RentalOrganizationSettings;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onSaved: (rental: Rental) => void;
};

export function RentalModal({
  rentalModel,
  rentals,
  rentalTrailers,
  rentalCustomers,
  organizationSettings,
  isOpen,
  setIsOpen,
  onSaved,
}: RentalModalProps) {
  const [saving, setSaving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState<"insurance" | "agreement" | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  // manual open/close of stage cards; reset when another rental is opened so
  // the defaults (current stage open, finished stages collapsed) apply again
  const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({});
  const [tab, setTab] = useState<"rental" | "documents">("rental");
  const rentalId = rentalModel.model?.id;
  useEffect(() => {
    setExpandedOverrides({});
    setTab("rental");
  }, [rentalId, isOpen]);

  // the dialog scrolls with the page, so a failed save must bring the
  // validation messages back into view from wherever the user was
  const errorsRef = useRef<HTMLDivElement>(null);
  const { errors } = rentalModel;
  useEffect(() => {
    if (errors?.length) {
      setTab("rental");
      errorsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [errors]);

  const rental = rentalModel.model;
  if (!rental) {
    return null;
  }

  const status = rental.rentalStatus;
  const cancelled = !rental.active && status < Constants.RENTAL_STATUS_ACTIVE;
  const isDraft = rentalModel.isNew || status === Constants.RENTAL_STATUS_DRAFT;
  // agreement terms stay editable until the agreement goes out
  const agreementEditable = !cancelled && status < Constants.RENTAL_STATUS_AGREEMENT_SENT;
  const unitLabel = isRentalTruckType(rental.trailerType) ? "Truck" : "Trailer";

  const reportActionError = (error: any, fallback: string) => {
    if (error?.response?.status === 422 && error.response.data?.length) {
      error.response.data.forEach((validation: { message: string }) =>
        enqueueSnackbar(validation.message, { variant: "error" })
      );
    } else {
      enqueueSnackbar(fallback, { variant: "error" });
    }
  };

  const saveRental = async (): Promise<Rental | null> => {
    const response = await rentalModel.save();
    return response ? (response.data as Rental) : null;
  };

  const runAction = async (
    action: (saved: Rental) => Promise<Rental>,
    successMessage: string,
    fallbackError: string,
    saveFirst: boolean
  ) => {
    if (saving) {
      return;
    }
    setSaving(true);
    let current: Rental = rental;
    try {
      if (saveFirst) {
        const saved = await saveRental();
        if (!saved) {
          return;
        }
        current = saved;
      }
      const result = await action(current);
      enqueueSnackbar(successMessage);
      onSaved(result);
      setIsOpen(false);
    } catch (error) {
      // keep the saved rental in the form so a failed send is retried
      // against it instead of creating a second rental
      if (current !== rental) {
        rentalModel.set(current);
      }
      reportActionError(error, fallbackError);
    } finally {
      setSaving(false);
    }
  };

  const post = (endpoint: { endpoint: string; params: string[] }, body?: any) =>
    async (saved: Rental): Promise<Rental> => {
      const response = await axios.post(formatEndpoint(endpoint, { rentalId: saved.id }), body);
      return response.data as Rental;
    };

  const saveOnly = async () => {
    if (saving) {
      return;
    }
    setSaving(true);
    try {
      const saved = await saveRental();
      if (saved) {
        enqueueSnackbar("Successfully saved the Rental");
        onSaved(saved);
        setIsOpen(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const previewPdf = (document: "insurance" | "agreement") => {
    axios
      .post(endpoints.rentals.previewPdf, rental, {
        responseType: "blob",
        params: document === "insurance" ? { document } : undefined,
      })
      .then((response) => {
        window.open(URL.createObjectURL(new Blob([response.data], { type: "application/pdf" })));
      })
      .catch(() => {
        enqueueSnackbar("The preview could not be generated. Please try again.", { variant: "error" });
      });
  };

  const rejectSubmission = () => {
    if (!rejectOpen) {
      return;
    }
    const endpoint =
      rejectOpen === "insurance" ? endpoints.rentals.rejectInsurance : endpoints.rentals.rejectAgreement;
    runAction(
      post(endpoint, { reason: rejectReason }),
      "The Customer has been asked to submit again",
      "The submission could not be rejected. Please try again.",
      false
    ).then(() => setRejectOpen(null));
  };

  // trailers without an ongoing rental (the one this rental holds stays listed)
  const busyTrailerIds = new Set(
    rentals.filter((r) => r.id !== rental.id && isOngoingRental(r)).map((r) => r.trailerId)
  );
  const trailerOptions = rentalTrailers
    .filter((trailer) => trailer.active && !busyTrailerIds.has(trailer.id))
    .map((trailer) => ({
      id: trailer.id,
      label: `${trailer.trailerUnitNumber} (${rentalTrailerType(trailer.trailerType)?.label || "-"})`,
    }));
  const selectedTrailerOption = trailerOptions.find((option) => option.id === rental.trailerId) || {
    id: 0,
    label: rental.trailerId ? `${rental.trailerUnitNumber}` : "",
  };

  const customerOptions = rentalCustomers
    .filter((customer) => customer.active)
    .map((customer) => ({
      id: customer.id,
      label: `${customer.firstName} ${customer.lastName} (${customer.companyName})`.trim(),
    }));
  const selectedCustomerOption = customerOptions.find((option) => option.id === rental.customerId) || {
    id: 0,
    label: rental.customerId ? `${rental.customerFirstName} ${rental.customerLastName}`.trim() : "",
  };

  const attachments = rental.insuranceAttachments || [];
  const libraryFileUrls = new Set(
    (organizationSettings?.blankForms || []).map((blankForm) => blankForm.fileUrl)
  );
  const timezone = organizationSettings?.companyTimeZone || "America/Chicago";

  const agreementTemplate = agreementTemplateForRental(organizationSettings, rental);
  const choiceGroups = collectChoiceGroups(agreementTemplate);
  const customVariables = collectCustomVariables(agreementTemplate);
  const insuranceCustomVariables = collectCustomVariables(organizationSettings?.insuranceFormTemplate || "");

  const formatDate = (iso: string) => DateTime.fromISO(iso).setZone(timezone).toFormat("MM/dd/yyyy");
  const rentalPeriodLabel = rental.rentalStartTime
    ? `${formatDate(rental.rentalStartTime)} - ${rental.rentalEndTime ? formatDate(rental.rentalEndTime) : "Open"}`
    : "";

  const formatDateTime = (iso: string) =>
    DateTime.fromISO(iso).setZone(timezone).toFormat("MM/dd/yyyy HH:mm");

  const stageChip = (confirmed: boolean, review: boolean, sent: boolean, confirmedLabel: string) => {
    if (confirmed) {
      return <Chip size="small" color="success" label={confirmedLabel} />;
    }
    if (review) {
      return <Chip size="small" color="primary" label="Review Needed" />;
    }
    if (sent) {
      return <Chip size="small" color="info" label="Sent" />;
    }
    return <Chip size="small" label="Not Sent" />;
  };
  const stageTimestamp = (confirmedAt: string | null, confirmedLabel: string, submittedAt: string | null, submittedLabel: string, sentAt: string | null) => {
    if (confirmedAt) {
      return `${confirmedLabel} ${formatDateTime(confirmedAt)}`;
    }
    if (submittedAt) {
      return `${submittedLabel} ${formatDateTime(submittedAt)}`;
    }
    if (sentAt) {
      return `Sent ${formatDateTime(sentAt)}`;
    }
    return undefined;
  };

  const insuranceChip = stageChip(
    status >= Constants.RENTAL_STATUS_INSURANCE_CONFIRMED,
    status === Constants.RENTAL_STATUS_INSURANCE_SUBMITTED,
    status === Constants.RENTAL_STATUS_INSURANCE_SENT,
    "Confirmed"
  );
  const insuranceTimestamp = stageTimestamp(
    rental.insuranceConfirmedAt,
    "Confirmed",
    rental.insuranceSubmittedAt,
    "Submitted",
    rental.insuranceSentAt
  );

  const agreementChip = stageChip(
    status >= Constants.RENTAL_STATUS_AGREEMENT_CONFIRMED,
    status === Constants.RENTAL_STATUS_AGREEMENT_SUBMITTED,
    status === Constants.RENTAL_STATUS_AGREEMENT_SENT,
    "Signed"
  );
  const agreementTimestamp = stageTimestamp(
    rental.agreementConfirmedAt,
    "Confirmed",
    rental.agreementSubmittedAt,
    "Signed",
    rental.agreementSentAt
  );

  const insuranceDefaultExpanded = status <= Constants.RENTAL_STATUS_INSURANCE_SUBMITTED;
  const agreementDefaultExpanded =
    status >= Constants.RENTAL_STATUS_INSURANCE_CONFIRMED &&
    status <= Constants.RENTAL_STATUS_AGREEMENT_SUBMITTED;
  const stageExpanded = (key: string, fallback: boolean) => expandedOverrides[key] ?? fallback;
  const toggleStage = (key: string, fallback: boolean) =>
    setExpandedOverrides((previous) => ({ ...previous, [key]: !(previous[key] ?? fallback) }));

  const statusBanner = () => {
    if (cancelled) {
      return <Alert severity="error">This rental has been cancelled.</Alert>;
    }
    switch (status) {
      case Constants.RENTAL_STATUS_INSURANCE_SENT:
        return (
          <Alert severity="info">
            The insurance request has been emailed to the Customer. Waiting for the completed forms.
            {rental.insuranceRejectionReason && ` Last submission was rejected: ${rental.insuranceRejectionReason}`}
          </Alert>
        );
      case Constants.RENTAL_STATUS_INSURANCE_SUBMITTED:
        return <Alert severity="info">The Customer submitted the insurance forms. Review them below.</Alert>;
      case Constants.RENTAL_STATUS_INSURANCE_CONFIRMED:
        return <Alert severity="info">Insurance confirmed. Fill in the lease terms and send the agreement.</Alert>;
      case Constants.RENTAL_STATUS_AGREEMENT_SENT:
        return (
          <Alert severity="info">
            The agreement has been emailed to the Customer. Waiting for their signature.
            {rental.agreementRejectionReason && ` Last submission was rejected: ${rental.agreementRejectionReason}`}
          </Alert>
        );
      case Constants.RENTAL_STATUS_AGREEMENT_SUBMITTED:
        return <Alert severity="info">The Customer signed the agreement and uploaded their ID. Review below.</Alert>;
      case Constants.RENTAL_STATUS_AGREEMENT_CONFIRMED:
        return (
          <Alert severity="success">
            Agreement signed and confirmed. Waiting for the yard to complete the Outbound form in the
            app - the rental becomes Active once it is signed at pick up.
          </Alert>
        );
      case Constants.RENTAL_STATUS_ACTIVE:
        return <Alert severity="success">The unit is rented out.</Alert>;
      case Constants.RENTAL_STATUS_RETURNED:
        return <Alert severity="info">The unit has been returned. Open it from the Rentals list to complete the rental.</Alert>;
      case Constants.RENTAL_STATUS_COMPLETED:
        return <Alert severity="success">This rental is completed.</Alert>;
      default:
        return null;
    }
  };

  const actionButtons = () => {
    if (cancelled) {
      return undefined;
    }
    const cancelButton = !rentalModel.isNew && status < Constants.RENTAL_STATUS_ACTIVE && (
      <Button
        variant="outlined"
        color="error"
        disabled={saving}
        startIcon={<Iconify icon="mingcute:close-line" />}
        onClick={() =>
          runAction(
            post(endpoints.rentals.cancel),
            "Rental cancelled",
            "The rental could not be cancelled. Please try again.",
            false
          )
        }
      >
        Cancel Rental
      </Button>
    );

    if (isDraft) {
      return (
        <Stack direction="row" gap={1}>
          {cancelButton}
          <Button
            variant="outlined"
            color="info"
            disabled={saving || rentalModel.loading}
            startIcon={<Iconify icon="basil:save-solid" />}
            onClick={saveOnly}
          >
            Save as Draft
          </Button>
        </Stack>
      );
    }
    if (status === Constants.RENTAL_STATUS_INSURANCE_SUBMITTED) {
      return (
        <Stack direction="row" gap={1}>
          {cancelButton}
          <Button
            variant="outlined"
            color="error"
            disabled={saving}
            startIcon={<Iconify icon="mingcute:close-line" />}
            onClick={() => {
              setRejectReason("");
              setRejectOpen("insurance");
            }}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={saving}
            startIcon={<Iconify icon="eva:checkmark-fill" />}
            onClick={() =>
              runAction(
                post(endpoints.rentals.confirmInsurance),
                "Insurance confirmed. You can now send the Agreement",
                "The insurance could not be confirmed. Please try again.",
                false
              )
            }
          >
            Confirm Insurance
          </Button>
        </Stack>
      );
    }
    if (status === Constants.RENTAL_STATUS_INSURANCE_CONFIRMED) {
      return (
        <Stack direction="row" gap={1}>
          {cancelButton}
          <Button
            variant="contained"
            color="info"
            disabled={saving}
            startIcon={<Iconify icon="ic:round-email" />}
            onClick={() =>
              runAction(
                post(endpoints.rentals.sendAgreement),
                "The Agreement has been emailed to the Customer for signing",
                "The Agreement could not be sent. Please try again.",
                true
              )
            }
          >
            Send Agreement
          </Button>
        </Stack>
      );
    }
    if (status === Constants.RENTAL_STATUS_AGREEMENT_SUBMITTED) {
      return (
        <Stack direction="row" gap={1}>
          {cancelButton}
          <Button
            variant="outlined"
            color="error"
            disabled={saving}
            startIcon={<Iconify icon="mingcute:close-line" />}
            onClick={() => {
              setRejectReason("");
              setRejectOpen("agreement");
            }}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={saving}
            startIcon={<Iconify icon="eva:checkmark-fill" />}
            onClick={() =>
              runAction(
                post(endpoints.rentals.confirmAgreement),
                "Agreement confirmed. The signed copy has been emailed to the Customer",
                "The agreement could not be confirmed. Please try again.",
                false
              )
            }
          >
            Confirm Agreement
          </Button>
        </Stack>
      );
    }
    // Ready for Outbound has no web action - the yard completes the outbound
    // in the app, the office is only waiting
    return cancelButton || undefined;
  };

  const showSave = isDraft || (agreementEditable && status >= Constants.RENTAL_STATUS_INSURANCE_CONFIRMED);

  const sendInsuranceRequest = () =>
    runAction(
      post(endpoints.rentals.sendInsurance),
      "The Insurance Request has been emailed to the Customer",
      "The Insurance Request could not be sent. Please try again.",
      true
    );

  let submitAction: VoidFunction | undefined;
  let submitLabel = "Save";
  let submitIcon = <Iconify icon="basil:save-solid" />;
  let submitColor: "success" | "info" = "info";
  if (isDraft && !cancelled) {
    submitAction = sendInsuranceRequest;
    submitLabel = "Send Insurance Request";
    submitIcon = <Iconify icon="ic:round-email" />;
    submitColor = "success";
  } else if (showSave) {
    submitAction = saveOnly;
  }

  return (
    <SimpleDialog
      title={<>{rentalModel.isNew ? "New Rental" : `Rental - ${unitLabel} ${rental.trailerUnitNumber}`}</>}
      open={isOpen}
      size="medium"
      onCancel={() => {
        setIsOpen(false);
      }}
      onSubmit={submitAction}
      submitDisabled={rentalModel.loading || saving}
      submitButton={submitLabel}
      submitButtonIcon={submitIcon}
      submitButtonColor={submitColor}
      buttons={actionButtons()}
    >
      <Box ref={errorsRef} sx={{ scrollMarginTop: 80 }}>
        <DisplayErrors savingData={rentalModel} />
      </Box>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="rental" label="Rental" />
        <Tab value="documents" label="Documents" />
      </Tabs>

      {tab === "documents" && <RentalDocuments rental={rental} unitLabel={unitLabel} timezone={timezone} />}

      <Stack direction="column" gap={2} sx={{ display: tab === "documents" ? "none" : undefined }}>
        {!rentalModel.isNew && !cancelled && (
          <Stepper
            activeStep={stageStep(status)}
            alternativeLabel
            sx={{
              pt: 1,
              "& .MuiStepIcon-root:not(.Mui-active):not(.Mui-completed)": {
                color: "#dedede",
                "& .MuiStepIcon-text": { fill: (theme) => theme.palette.text.secondary },
              },
            }}
          >
            {STAGE_STEPS.map((step) => (
              <Step key={step}>
                <StepLabel>{step}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {statusBanner()}

        {rentalModel.isNew && (
          <Typography variant="body2" color="text.secondary">
            The rental starts with an insurance request sent to the customer.
          </Typography>
        )}

        {rentalModel.isNew ? (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TruxAutocompleteField
                label="Unit *"
                options={trailerOptions}
                selected={selectedTrailerOption}
                freeSolo={false}
                noOptionsText="No available units found"
                onChange={(selection) => {
                  if (!selection) {
                    return;
                  }
                  const trailer = rentalTrailers.find((t) => t.id === selection.id);
                  if (trailer) {
                    rentalModel.update((r) => {
                      r.trailerId = trailer.id;
                      r.trailerUnitNumber = trailer.trailerUnitNumber;
                      r.make = trailer.make;
                      r.model = trailer.model;
                      r.year = trailer.year;
                      r.vin = trailer.vin;
                      r.licensePlateState = trailer.licensePlateState;
                      r.licensePlate = trailer.licensePlate;
                      r.trailerType = trailer.trailerType;
                    });
                  }
                }}
                onClear={() => {
                  rentalModel.update((r) => {
                    r.trailerId = 0;
                  });
                }}
              />
              <TruxAutocompleteField
                label="Customer *"
                options={customerOptions}
                selected={selectedCustomerOption}
                freeSolo={false}
                noOptionsText="No customers found"
                onChange={(selection) => {
                  if (!selection) {
                    return;
                  }
                  const customer = rentalCustomers.find((c) => c.id === selection.id);
                  if (customer) {
                    rentalModel.update((r) => {
                      r.customerId = customer.id;
                      r.customerCompanyName = customer.companyName;
                      r.customerFirstName = customer.firstName;
                      r.customerLastName = customer.lastName;
                      r.customerAddress = { ...customer.address };
                      r.customerPhone = customer.phone;
                      r.customerEmail = customer.email;
                      r.customerDotNumber = customer.dotNumber;
                      r.customerMcNumber = customer.mcNumber;
                      r.customerFeinNumber = customer.feinNumber;
                    });
                  }
                }}
                onClear={() => {
                  rentalModel.update((r) => {
                    r.customerId = 0;
                  });
                }}
              />
            </Box>

            {(rental.trailerId > 0 || rental.customerId > 0) && (
              <Stack gap={0.25}>
                {rental.trailerId > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {`${unitLabel}: ${[rental.make, rental.model, rental.year].filter(Boolean).join(" ") || rental.trailerUnitNumber}`}
                  </Typography>
                )}
                {rental.customerId > 0 && rental.customerEmail && (
                  <Typography variant="caption" color="text.secondary">
                    {`The request will be emailed to ${rental.customerEmail}`}
                  </Typography>
                )}
                {rental.customerId > 0 && !rental.customerEmail && (
                  <Typography variant="caption" color="error.main">
                    This customer has no email address. Add it on the Customers page before sending
                  </Typography>
                )}
              </Stack>
            )}
          </>
        ) : (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
              gap: 2,
            }}
          >
            <InfoItem
              label="Customer"
              value={`${rental.customerFirstName} ${rental.customerLastName}`.trim()}
            />
            <InfoItem label="Company" value={rental.customerCompanyName} />
            <InfoItem label="Email" value={rental.customerEmail} />
            <InfoItem label="Phone" value={rental.customerPhone} />
            <InfoItem label={unitLabel} value={rental.trailerUnitNumber} />
            <InfoItem
              label="Make / Model / Year"
              value={[rental.make, rental.model, rental.year].filter(Boolean).join(" ")}
            />
            <InfoItem label="VIN" value={rental.vin} />
            <InfoItem
              label="License Plate"
              value={`${rental.licensePlateState} ${rental.licensePlate}`.trim()}
            />
          </Box>
        )}

        {rentalModel.isNew ? (
          <StageCard
            title="Insurance"
            chip={null}
            expanded={stageExpanded("insurance", false)}
            onToggle={() => toggleStage("insurance", false)}
          >
            <Typography variant="caption" color="text.secondary">
              Sent to the customer first, with the fields and forms below
            </Typography>
        {isDraft ? (
          <>
            {insuranceCustomVariables.length > 0 && (
              <Stack gap={1.5}>
                <Stack>
                  <Typography variant="subtitle2">Insurance Fields</Typography>
                  <Typography variant="caption" color="text.secondary">
                    These custom fields are printed in the insurance request where you placed them.
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  {insuranceCustomVariables.map(({ name, optional }) => (
                    <TruxSimpleTextField
                      key={name}
                      label={optional ? name : `${name} *`}
                      value={rental.customValues?.[name] || ""}
                      onChange={(value) => rentalModel.update((r) => {
                        r.customValues = { ...(r.customValues || {}), [name]: value };
                      })}
                    />
                  ))}
                </Box>
              </Stack>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} gap={2}>
              <Stack direction="row" alignItems="center" gap={1}>
                <TruxCheckboxField modelSavingData={rentalModel} field="pdOnCustomerInsurance" />
                <Typography variant="body2">PD on Customer&apos;s Insurance</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary">PD Form (PDF)</Typography>
                <RentalFileField
                  fileUrl={rental.pdFile}
                  onChange={(fileUrl) => rentalModel.update((r) => { r.pdFile = fileUrl; })}
                />
              </Stack>
            </Stack>
          </>
        ) : (
          <Stack direction="row" alignItems="flex-start" gap={2} flexWrap="wrap">
            <Box
              sx={{
                flexGrow: 1,
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              {insuranceCustomVariables
                .filter(({ name, optional }) => !optional || rental.customValues?.[name])
                .map(({ name }) => (
                  <InfoItem key={name} label={name} value={rental.customValues?.[name]} />
                ))}
              <InfoItem
                label="PD on Customer's Insurance"
                value={rental.pdOnCustomerInsurance ? "Yes" : "No"}
              />
            </Box>
            {rental.pdFile && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                href={rental.pdFile}
                target="_blank"
                rel="noopener"
                startIcon={<Iconify icon="solar:document-text-bold" width={16} />}
              >
                PD Form
              </Button>
            )}
          </Stack>
        )}

        <Stack gap={1}>
          <Typography variant="subtitle2">
            {isDraft ? "Forms the Customer must populate" : "Customer Forms"}
          </Typography>
          {attachments.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No forms attached. The customer will only be asked to name their insurer.
            </Typography>
          )}
          {attachments.map((attachment, index) => (
            isDraft ? (
              <Stack key={index} direction="row" gap={2} alignItems="center">
                {libraryFileUrls.has(attachment.blankUrl) && (
                  <Chip size="small" label="Library" sx={{ flexShrink: 0 }} />
                )}
                <Box flex={1}>
                  <TruxSimpleTextField
                    label="Form Name"
                    value={attachment.name}
                    onChange={(name) => rentalModel.update((r) => {
                      r.insuranceAttachments = (r.insuranceAttachments || []).map((entry, i) =>
                        i === index ? { ...entry, name } : entry);
                    })}
                  />
                </Box>
                <Box flex={1}>
                  <RentalFileField
                    pdfOnly
                    fileUrl={attachment.blankUrl}
                    onChange={(blankUrl) => rentalModel.update((r) => {
                      r.insuranceAttachments = (r.insuranceAttachments || []).map((entry, i) =>
                        i === index ? { ...entry, blankUrl } : entry);
                    })}
                  />
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => rentalModel.update((r) => {
                    r.insuranceAttachments = (r.insuranceAttachments || []).filter((_entry, i) => i !== index);
                  })}
                >
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </Stack>
            ) : (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Iconify
                    icon={attachment.returnedUrl ? "solar:check-circle-bold" : "solar:document-bold"}
                    width={20}
                    color={attachment.returnedUrl ? "success.main" : "text.disabled"}
                  />
                  <Typography variant="subtitle2">{attachment.name}</Typography>
                </Stack>
                {attachment.returnedUrl ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    href={attachment.returnedUrl}
                    target="_blank"
                    rel="noopener"
                    startIcon={<Iconify icon="solar:document-text-bold" width={16} />}
                  >
                    View Completed Form
                  </Button>
                ) : (
                  <Chip size="small" label="Not returned yet" />
                )}
              </Stack>
            )
          ))}
          {isDraft && (
            <Stack direction="row" gap={1}>
              <Button
                size="small"
                startIcon={<Iconify icon="mingcute:add-line" />}
                onClick={() => rentalModel.update((r) => {
                  r.insuranceAttachments = [
                    ...(r.insuranceAttachments || []),
                    { name: "", blankUrl: "", returnedUrl: "", returnedAt: null },
                  ];
                })}
              >
                Add Form
              </Button>
              <Button size="small" variant="text" onClick={() => previewPdf("insurance")}>
                Preview Insurance PDF
              </Button>
            </Stack>
          )}
        </Stack>
          </StageCard>
        ) : (
          <StageCard
            title="Insurance"
            chip={insuranceChip}
            timestamp={insuranceTimestamp}
            expanded={stageExpanded("insurance", insuranceDefaultExpanded)}
            onToggle={() => toggleStage("insurance", insuranceDefaultExpanded)}
          >
            {rental.insurancePdfUrl && (
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  href={rental.insurancePdfUrl}
                  target="_blank"
                  rel="noopener"
                  startIcon={<Iconify icon="solar:document-text-bold" width={16} />}
                >
                  Insurance Request PDF
                </Button>
              </Stack>
            )}
        {isDraft ? (
          <>
            {insuranceCustomVariables.length > 0 && (
              <Stack gap={1.5}>
                <Stack>
                  <Typography variant="subtitle2">Insurance Fields</Typography>
                  <Typography variant="caption" color="text.secondary">
                    These custom fields are printed in the insurance request where you placed them.
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  {insuranceCustomVariables.map(({ name, optional }) => (
                    <TruxSimpleTextField
                      key={name}
                      label={optional ? name : `${name} *`}
                      value={rental.customValues?.[name] || ""}
                      onChange={(value) => rentalModel.update((r) => {
                        r.customValues = { ...(r.customValues || {}), [name]: value };
                      })}
                    />
                  ))}
                </Box>
              </Stack>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} gap={2}>
              <Stack direction="row" alignItems="center" gap={1}>
                <TruxCheckboxField modelSavingData={rentalModel} field="pdOnCustomerInsurance" />
                <Typography variant="body2">PD on Customer&apos;s Insurance</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary">PD Form (PDF)</Typography>
                <RentalFileField
                  fileUrl={rental.pdFile}
                  onChange={(fileUrl) => rentalModel.update((r) => { r.pdFile = fileUrl; })}
                />
              </Stack>
            </Stack>
          </>
        ) : (
          <Stack direction="row" alignItems="flex-start" gap={2} flexWrap="wrap">
            <Box
              sx={{
                flexGrow: 1,
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              {insuranceCustomVariables
                .filter(({ name, optional }) => !optional || rental.customValues?.[name])
                .map(({ name }) => (
                  <InfoItem key={name} label={name} value={rental.customValues?.[name]} />
                ))}
              <InfoItem
                label="PD on Customer's Insurance"
                value={rental.pdOnCustomerInsurance ? "Yes" : "No"}
              />
            </Box>
            {rental.pdFile && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                href={rental.pdFile}
                target="_blank"
                rel="noopener"
                startIcon={<Iconify icon="solar:document-text-bold" width={16} />}
              >
                PD Form
              </Button>
            )}
          </Stack>
        )}

        <Stack gap={1}>
          <Typography variant="subtitle2">
            {isDraft ? "Forms the Customer must populate" : "Customer Forms"}
          </Typography>
          {attachments.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No forms attached. The customer will only be asked to name their insurer.
            </Typography>
          )}
          {attachments.map((attachment, index) => (
            isDraft ? (
              <Stack key={index} direction="row" gap={2} alignItems="center">
                {libraryFileUrls.has(attachment.blankUrl) && (
                  <Chip size="small" label="Library" sx={{ flexShrink: 0 }} />
                )}
                <Box flex={1}>
                  <TruxSimpleTextField
                    label="Form Name"
                    value={attachment.name}
                    onChange={(name) => rentalModel.update((r) => {
                      r.insuranceAttachments = (r.insuranceAttachments || []).map((entry, i) =>
                        i === index ? { ...entry, name } : entry);
                    })}
                  />
                </Box>
                <Box flex={1}>
                  <RentalFileField
                    pdfOnly
                    fileUrl={attachment.blankUrl}
                    onChange={(blankUrl) => rentalModel.update((r) => {
                      r.insuranceAttachments = (r.insuranceAttachments || []).map((entry, i) =>
                        i === index ? { ...entry, blankUrl } : entry);
                    })}
                  />
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => rentalModel.update((r) => {
                    r.insuranceAttachments = (r.insuranceAttachments || []).filter((_entry, i) => i !== index);
                  })}
                >
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </Stack>
            ) : (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Iconify
                    icon={attachment.returnedUrl ? "solar:check-circle-bold" : "solar:document-bold"}
                    width={20}
                    color={attachment.returnedUrl ? "success.main" : "text.disabled"}
                  />
                  <Typography variant="subtitle2">{attachment.name}</Typography>
                </Stack>
                {attachment.returnedUrl ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    href={attachment.returnedUrl}
                    target="_blank"
                    rel="noopener"
                    startIcon={<Iconify icon="solar:document-text-bold" width={16} />}
                  >
                    View Completed Form
                  </Button>
                ) : (
                  <Chip size="small" label="Not returned yet" />
                )}
              </Stack>
            )
          ))}
          {isDraft && (
            <Stack direction="row" gap={1}>
              <Button
                size="small"
                startIcon={<Iconify icon="mingcute:add-line" />}
                onClick={() => rentalModel.update((r) => {
                  r.insuranceAttachments = [
                    ...(r.insuranceAttachments || []),
                    { name: "", blankUrl: "", returnedUrl: "", returnedAt: null },
                  ];
                })}
              >
                Add Form
              </Button>
              <Button size="small" variant="text" onClick={() => previewPdf("insurance")}>
                Preview Insurance PDF
              </Button>
            </Stack>
          )}
        </Stack>
        {(rental.customerInsuranceDocuments || []).length > 0 && (
          <Stack gap={1}>
            <Typography variant="subtitle2">Additional Customer Documents</Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {(rental.customerInsuranceDocuments || []).map((documentUrl, index) => (
                <Button
                  key={index}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  href={documentUrl}
                  target="_blank"
                  rel="noopener"
                  startIcon={<Iconify icon="solar:document-text-bold" width={16} />}
                >
                  {`Document ${index + 1}`}
                </Button>
              ))}
            </Stack>
          </Stack>
        )}
          </StageCard>
        )}

        {/* staff populates everything in the agreement; the customer only reads and signs */}
        {!rentalModel.isNew && status >= Constants.RENTAL_STATUS_INSURANCE_CONFIRMED && (
          <StageCard
            title="Agreement"
            chip={agreementChip}
            timestamp={agreementTimestamp}
            expanded={stageExpanded("agreement", agreementDefaultExpanded)}
            onToggle={() => toggleStage("agreement", agreementDefaultExpanded)}
          >
            {(rental.signedAgreementPdfUrl || rental.agreementContractInfo?.contractPdfUrl) && (
              <Stack direction="row" gap={1} flexWrap="wrap">
                {rental.signedAgreementPdfUrl ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    href={rental.signedAgreementPdfUrl}
                    target="_blank"
                    rel="noopener"
                    startIcon={<Iconify icon="solar:document-text-bold" width={16} />}
                  >
                    Signed Agreement PDF
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    href={rental.agreementContractInfo.contractPdfUrl}
                    target="_blank"
                    rel="noopener"
                    startIcon={<Iconify icon="solar:document-text-bold" width={16} />}
                  >
                    Agreement PDF
                  </Button>
                )}
              </Stack>
            )}
            {agreementEditable && (
              <Typography variant="caption" color="text.secondary">
                Fill in the lease terms and selections. The customer only reads and signs.
              </Typography>
            )}
            {agreementEditable ? (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
                    {/* the default agreement is the empty value, so the select
                        must render it instead of MUI's blank "no value" */}
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Agreement"
                      value={rental.agreementName || ""}
                      onChange={(event) => rentalModel.update((r) => {
                        r.agreementName = event.target.value;
                      })}
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">Default (by unit type)</MenuItem>
                      {(organizationSettings?.namedAgreements || []).map((named) => (
                        <MenuItem value={named.name} key={named.name}>{named.name}</MenuItem>
                      ))}
                    </TextField>
                  </Box>


                  <TruxSimpleDateTimeField
                    label="Rental Start *"
                    timezone={timezone}
                    limitWidth={false}
                    value={rental.rentalStartTime || undefined}
                    onChange={(date) => rentalModel.update((r) => {
                      r.rentalStartTime = date || null;
                    })}
                  />
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box flex={1}>
                      <TruxSimpleDateTimeField
                        label="Rental End (empty = open)"
                        timezone={timezone}
                        limitWidth={false}
                        value={rental.rentalEndTime || undefined}
                        onChange={(date) => rentalModel.update((r) => {
                          r.rentalEndTime = date || null;
                        })}
                      />
                    </Box>
                    {rental.rentalEndTime && (
                      <IconButton
                        size="small"
                        title="Make the rental open-ended"
                        onClick={() => rentalModel.update((r) => { r.rentalEndTime = null; })}
                      >
                        <Iconify icon="mingcute:close-line" width={18} />
                      </IconButton>
                    )}
                  </Stack>
                </Box>

                {customVariables.length > 0 && (
                  <Stack gap={1.5}>
                    <Stack>
                      <Typography variant="subtitle2">Agreement Fields</Typography>
                      <Typography variant="caption" color="text.secondary">
                        These custom fields are printed in the agreement where you placed them.
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      {customVariables.map(({ name, optional }) => (
                        <TruxSimpleTextField
                          key={name}
                          label={optional ? name : `${name} *`}
                          value={rental.customValues?.[name] || ""}
                          onChange={(value) => rentalModel.update((r) => {
                            r.customValues = { ...(r.customValues || {}), [name]: value };
                          })}
                        />
                      ))}
                    </Box>
                  </Stack>
                )}

                {choiceGroups.length > 0 && (
                  <Stack gap={1}>
                    <Typography variant="subtitle2">Agreement Selections</Typography>
                    <Typography variant="caption" color="text.secondary">
                      These choices are printed in the agreement with your selection checked. The customer
                      cannot change them.
                    </Typography>
                    {choiceGroups.map((group, index) => (
                      <Box
                        key={group.id}
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>{`Choice ${index + 1}`}</Typography>
                        <RadioGroup
                          value={rental.choiceSelections?.[group.id] || ""}
                          onChange={(event) => rentalModel.update((r) => {
                            r.choiceSelections = { ...(r.choiceSelections || {}), [group.id]: event.target.value };
                          })}
                        >
                          {group.options.map((option, optionIndex) => (
                            <FormControlLabel
                              key={optionIndex}
                              value={String(optionIndex)}
                              control={<Radio size="small" />}
                              label={<Typography variant="body2">{option}</Typography>}
                            />
                          ))}
                        </RadioGroup>
                      </Box>
                    ))}
                  </Stack>
                )}

                <Box>
                  <Button size="small" variant="text" onClick={() => previewPdf("agreement")}>
                    Preview Agreement PDF
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
                    gap: 2,
                  }}
                >
                  <InfoItem label="Agreement" value={rental.agreementName || "Default"} />
                  <InfoItem label="Rental Period" value={rentalPeriodLabel} />
                  {customVariables
                    .filter(({ name, optional }) => !optional || rental.customValues?.[name])
                    .map(({ name }) => (
                      <InfoItem key={name} label={name} value={rental.customValues?.[name]} />
                    ))}
                </Box>

                {choiceGroups.length > 0 && (
                  <Stack gap={1}>
                    <Typography variant="subtitle2">Agreement Selections</Typography>
                    {choiceGroups.map((group) => {
                      const selectedIndex = Number(rental.choiceSelections?.[group.id] ?? -1);
                      const selected = group.options[selectedIndex];
                      return (
                        <Stack key={group.id} direction="row" alignItems="flex-start" gap={1}>
                          <Iconify
                            icon="eva:checkmark-fill"
                            width={18}
                            color={selected ? "success.main" : "text.disabled"}
                          />
                          <Typography variant="body2">{selected || "-"}</Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </>
            )}
            {Object.keys(rental.signerFieldValues || {}).length > 0 && (
              <Stack gap={1}>
                <Typography variant="subtitle2">Filled in by the Customer</Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
                    gap: 2,
                  }}
                >
                  {Object.entries(rental.signerFieldValues || {}).map(([name, fieldValue]) => (
                    <InfoItem key={name} label={name} value={fieldValue} />
                  ))}
                </Box>
              </Stack>
            )}
            {rental.agreementSignature && (
              <Stack gap={1}>
                <Typography variant="subtitle2">Customer Signature</Typography>
                <Box
                  sx={{
                    width: "fit-content",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "common.white",
                    p: 1,
                  }}
                >
                  <Box component="img" src={rental.agreementSignature} sx={{ maxWidth: 300, display: "block" }} />
                </Box>
              </Stack>
            )}
            {status === Constants.RENTAL_STATUS_AGREEMENT_SUBMITTED && (
              <Stack gap={1}>
                <Typography variant="subtitle2">Customer ID</Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {(rental.agreementIdPhotos || []).map((photoUrl) => (
                    <Link key={photoUrl} href={photoUrl} target="_blank" rel="noopener">
                      <Box
                        component="img"
                        src={photoUrl}
                        sx={{ width: 120, height: 90, objectFit: "cover", borderRadius: 1 }}
                      />
                    </Link>
                  ))}
                  {!(rental.agreementIdPhotos || []).length && (
                    <Typography variant="caption" color="text.secondary">No ID photos uploaded</Typography>
                  )}
                </Stack>
              </Stack>
            )}
          </StageCard>
        )}
      </Stack>

      <Dialog open={!!rejectOpen} onClose={() => !saving && setRejectOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject this submission?</DialogTitle>
        <DialogContent>
          <TruxSimpleTextField
            label="Reason (sent to the Customer)"
            value={rejectReason}
            onChange={setRejectReason}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={rejectSubmission} disabled={saving}>
            Reject &amp; Ask Again
          </Button>
        </DialogActions>
      </Dialog>
    </SimpleDialog>
  );
}
